import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const completeLogin = async (access_token) => {
    const userResponse = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } });
    login(userResponse.data, access_token);
    toast({ title: 'Login successful!', description: `Welcome back, ${userResponse.data.name}!` });
    const govRoles = ['agency_admin', 'division_admin', 'purchaser', 'approver', 'finance_officer'];
    const uskeStaff = ['uske_front_desk', 'uske_finance_officer'];
    const role = userResponse.data.role;
    if (role === 'super_admin' || role === 'admin') {
      navigate('/admin');
    } else if (role === 'uske_delivery') {
      navigate('/delivery');
    } else if (uskeStaff.includes(role)) {
      navigate('/portal/fulfilment');
    } else if (govRoles.includes(role)) {
      navigate('/portal/dashboard');
    } else if (role === 'buyer') {
      navigate('/dashboard');
    } else {
      navigate('/account');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      if (response.data.mfa_required) {
        setMfaStep({ mfa_token: response.data.mfa_token, email: response.data.email });
        toast({ title: 'Two-factor required', description: 'Enter your authenticator code.' });
      } else {
        await completeLogin(response.data.access_token);
      }
    } catch (error) {
      toast({ title: 'Login failed', description: error.response?.data?.detail || 'Invalid email or password', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login/mfa`, { mfa_token: mfaStep.mfa_token, code: mfaCode });
      await completeLogin(res.data.access_token);
    } catch (error) {
      toast({ title: 'Invalid code', description: error.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md border-2">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Login to Your Account</CardTitle>
        </CardHeader>
        <CardContent>
          {mfaStep ? (
            <form onSubmit={submitMfa} className="space-y-4">
              <p className="text-sm text-slate-600">Two-factor authentication is enabled. Enter the 6-digit code for <b>{mfaStep.email}</b>.</p>
              <div>
                <Label htmlFor="mfa-code">Authenticator code</Label>
                <Input id="mfa-code" inputMode="numeric" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required autoFocus data-testid="login-mfa-code" />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading} data-testid="login-mfa-submit">
                {loading ? 'Verifying...' : 'Verify and continue'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => { setMfaStep(null); setMfaCode(''); }}>Back</Button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                disabled={loading}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          )}
          <div className="mt-6 text-center space-y-2">
            <p className="text-slate-600">
              <Link to="/forgot-password" className="text-purple-600 hover:underline font-medium" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </p>
            <p className="text-slate-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:underline font-semibold">
                Register here
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;